import { useEffect } from 'react';
import {
  Sparkles,
  Check,
  ThumbsUp,
  ThumbsDown,
  Plus,
  Minus,
  RefreshCw,
  ArrowRight,
  Loader2,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { useBrandKit } from '@/shared/context/useBrandKit';
import { useLocation } from '@/shared/hooks/useLocation';
import { strategyApi } from '../api/strategyApi';
import type { BrandKitOption, ArchetypeName } from '../api/strategyApi';
import { strategyKeys } from '../queryKeys';
import patterns from '@/shared/ui/styles/patterns.module.css';
import styles from './BrandKitPage.module.css';

const ARCHETYPE_DIRECTION: Record<ArchetypeName, string> = {
  Caregiver: 'warm',
  Everyman: 'trusted',
  Sage: 'clean',
  Hero: 'clean',
};

const DIRECTION_MARK: Record<string, string> = {
  warm: '◎',
  trusted: 'M',
  clean: '△',
};

function ProsCons({ pros, cons }: { pros: string[]; cons: string[] }) {
  return (
    <div className={styles.prosConsGrid}>
      <div>
        <div className={styles.prosHeader}>
          <ThumbsUp size={12} aria-hidden />
          Pros
        </div>
        {pros.map((p) => (
          <div key={p} className={styles.proItem}>
            <Plus size={11} className={styles.proIcon} aria-hidden />
            {p}
          </div>
        ))}
      </div>
      <div>
        <div className={styles.consHeader}>
          <ThumbsDown size={12} aria-hidden />
          Cons
        </div>
        {cons.map((c) => (
          <div key={c} className={styles.proItem}>
            <Minus size={11} className={styles.conIcon} aria-hidden />
            {c}
          </div>
        ))}
      </div>
    </div>
  );
}

function HexSwatches({ palette }: { palette: BrandKitOption['palette'] }) {
  return (
    <div className={styles.swatchRow}>
      {palette.map((color) => (
        <div
          key={color.role}
          className={styles.swatch}
          style={{ background: color.hex }}
          title={`${color.role}: ${color.hex}`}
        />
      ))}
    </div>
  );
}

function GeneratingState() {
  return (
    <div className={styles.generatingState}>
      <Loader2 size={20} className={styles.spinner} aria-hidden />
      <p className={styles.generatingText}>Generating your brand directions…</p>
      <p className={styles.generatingSubtext}>
        Tailoring palettes, taglines, and trade-offs to your archetype and positioning.
      </p>
    </div>
  );
}

export function BrandKitPage() {
  const { approved, selectedId: selected, setSelected, approve, unapprove } = useBrandKit();
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();

  const { data: brandProfileData } = useQuery({
    queryKey: strategyKeys.brandProfile(locationId),
    queryFn: () => strategyApi.getBrandProfile(locationId),
    enabled: !!locationId,
    staleTime: 10 * 60 * 1000,
  });

  const { data: brandKitData, isLoading: kitLoading } = useQuery({
    queryKey: strategyKeys.brandKit(locationId),
    queryFn: () => strategyApi.getBrandKit(locationId),
    enabled: !!locationId,
    staleTime: 30 * 60 * 1000,
  });

  const brandProfile = brandProfileData?.data ?? null;
  const options = brandKitData?.data?.options ?? null;
  const recommendedId = brandProfile ? ARCHETYPE_DIRECTION[brandProfile.primary] : null;
  const practiceName = currentLocation?.name ?? 'Your Practice';

  const generateMutation = useMutation({
    mutationFn: () => strategyApi.generateBrandKit(locationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: strategyKeys.brandKit(locationId) });
    },
  });

  // Auto-generate on first visit if brand profile exists but options haven't been generated
  useEffect(() => {
    if (!kitLoading && brandProfile && !options && !generateMutation.isPending) {
      generateMutation.mutate();
    }
  }, [kitLoading, brandProfile, options]);

  const isGenerating = generateMutation.isPending;
  const selectedOption = options?.find((o) => o.id === selected) ?? null;
  const selectedName = selectedOption?.name ?? selected;

  return (
    <div className={patterns.page}>
      <PageHeader
        title="Brand Kit"
        subtitle="Three directions from your positioning. Weigh the trade-offs, pick one, and approve — everything downstream uses the approved kit."
        actions={
          approved ? (
            <span className={patterns.pillSuccess}>
              <Check size={12} aria-hidden /> Approved · {selectedName}
            </span>
          ) : (
            <span className={patterns.pillWarning}>Not yet approved</span>
          )
        }
      />

      {brandProfile && (
        <Callout icon={Sparkles} variant="accent">
          <div className={styles.sourceBanner}>
            From: <strong>{brandProfile.label}</strong>
            {' · '}voice <strong>{brandProfile.toneOfVoice}</strong>
          </div>
        </Callout>
      )}

      {isGenerating || (kitLoading && !options) ? (
        <GeneratingState />
      ) : options ? (
        <>
          <div className={styles.directionGrid}>
            {options.map((d) => {
              const isSelected = selected === d.id;
              const isRecommended = recommendedId === d.id;
              return (
                <div
                  key={d.id}
                  className={`${styles.directionCard} ${isSelected ? styles.directionCardSelected : ''} ${isRecommended && !isSelected ? styles.directionCardRecommended : ''}`}
                >
                  <div
                    className={styles.preview}
                    style={{ background: d.previewBg, color: d.previewFg }}
                  >
                    <div className={styles.previewInner}>
                      <span className={styles.previewMark}>{DIRECTION_MARK[d.id]}</span>
                      <span className={styles.previewName}>{practiceName}</span>
                    </div>
                  </div>

                  <div className={styles.directionHeader}>
                    <span className={patterns.sectionTitle}>{d.name}</span>
                    <div className={styles.directionBadges}>
                      {isRecommended && (
                        <span className={patterns.pillAccent}>
                          <Sparkles size={10} aria-hidden /> Recommended
                        </span>
                      )}
                      {isSelected && (
                        <span className={patterns.pillSuccess}>
                          <Check size={11} aria-hidden /> Selected
                        </span>
                      )}
                    </div>
                  </div>

                  <p className={styles.tagline}>{d.tagline}</p>
                  <div className={styles.directionVibe}>{d.vibe}</div>

                  <HexSwatches palette={d.palette} />
                  <div className={styles.typeLabel}>{d.typeLabel}</div>

                  <ProsCons pros={d.pros} cons={d.cons} />

                  <Button
                    variant={isSelected ? 'secondary' : 'primary'}
                    leftIcon={isSelected ? <Check size={14} /> : <ArrowRight size={14} />}
                    size="sm"
                    onClick={() => setSelected(d.id)}
                  >
                    {isSelected ? 'Selected' : 'Choose this direction'}
                  </Button>
                </div>
              );
            })}
          </div>

          <div className={`${patterns.panel} ${patterns.cardRow}`}>
            <p className={styles.approveText}>
              Approving locks <strong>{selectedName}</strong> as the source of truth for landing
              pages, print, mailers, the call agent persona, and partner outreach.
            </p>
            <div className={styles.approveActions}>
              {approved ? (
                <Button variant="secondary" leftIcon={<RefreshCw size={14} />} onClick={unapprove}>
                  Un-approve / change
                </Button>
              ) : (
                <Button leftIcon={<Check size={14} />} onClick={approve}>
                  Approve Brand Kit
                </Button>
              )}
              <Button
                variant="ghost"
                leftIcon={<RefreshCw size={14} />}
                size="sm"
                onClick={() => generateMutation.mutate()}
                disabled={isGenerating}
              >
                Regenerate
              </Button>
            </div>
          </div>

          <p className={styles.footerNote}>
            Palettes are AI-generated from your archetype and positioning. Logo mark goes through a
            vector export / light designer pass before print.
          </p>
        </>
      ) : null}
    </div>
  );
}
